import axios from "axios";
import apiConfig from "@/api/config";
import forge from "node-forge";

const VERSION = 1;
const HEADER_SESSION = "X-Crypto-Session";
const HEADER_REQUEST_ID = "X-Crypto-Request-Id";
const HEADER_KID = "X-Crypto-Kid";
const SESSION_RENEW_WINDOW_MS = 30000;

const createCryptoError = (code, message) => {
  const error = new Error(message);
  error.code = code;
  return error;
};

const protectedEndpoints = new Set([
  "POST /admin-api/system/auth/loginNoCode",
  "POST /admin-api/system/auth/login",
  "POST /admin-api/system/auth/sms-login",
  "POST /admin-api/system/auth/send-sms-code",
  "POST /admin-api/system/auth/sendSmsCodeOfRegister",
  "POST /admin-api/system/auth/sms-password",
  "POST /admin-api/system/auth/social-login",
  "POST /admin-api/system/auth/refresh-token",
  "POST /web-api/web/gateway/userCreate",
  "GET /web-api/web/gateway/getUser",
  "POST /web-api/web/gateway/updateUser",
  "POST /web-api/web/gateway/updateUserPassword",
  "GET /web-api/web/gateway/getOneSingleInstrument",
  "POST /web-api/web/gateway/orderCreate",
  "GET /web-api/web/gateway/orderGetOne",
  "POST /web-api/web/gateway/orderUpdate",
  "POST /web-api/web/gateway/orderPage",
  "POST /web-api/web/gateway/orderPageStatus",
]);

let activeSession = null;
let sessionPromise = null;
let sessionGeneration = 0;

const toBase64 = (bytes) => {
  let binary = "";
  const chunkSize = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  }
  return window.btoa(binary);
};

const fromBase64 = (value) => {
  const binary = window.atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
};

const getCryptoConfiguration = () => {
  const kid = process.env.VUE_APP_CRYPTO_KID;
  const publicKey = process.env.VUE_APP_CRYPTO_PUBLIC_KEY;
  if (!window.crypto || typeof window.crypto.getRandomValues !== "function") {
    throw createCryptoError(
      "BUSINESS_CRYPTO_UNSUPPORTED",
      "当前浏览器不支持安全随机数生成，无法执行业务加密。"
    );
  }
  if (!kid || !publicKey) {
    throw createCryptoError(
      "BUSINESS_CRYPTO_MISCONFIGURED",
      "业务加密公钥未配置，已拒绝发送敏感请求。"
    );
  }
  return { kid, publicKey };
};

const hasWebCrypto = () => Boolean(window.crypto && window.crypto.subtle);

const bytesToBinary = (bytes) => {
  let binary = "";
  const chunkSize = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  }
  return binary;
};

const binaryToBytes = (binary) => {
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
};

const wrapAesKeyWithForge = (publicKey, aesBytes) => {
  const publicKeyAsn1 = forge.asn1.fromDer(bytesToBinary(fromBase64(publicKey)));
  const rsaKey = forge.pki.publicKeyFromAsn1(publicKeyAsn1);
  return rsaKey.encrypt(bytesToBinary(aesBytes), "RSA-OAEP", {
    md: forge.md.sha256.create(),
    mgf1: { md: forge.md.sha256.create() },
  });
};

const encryptWithForge = (keyBytes, iv, additionalData, plaintext) => {
  const cipher = forge.cipher.createCipher("AES-GCM", bytesToBinary(keyBytes));
  cipher.start({
    iv: bytesToBinary(iv),
    additionalData: bytesToBinary(additionalData),
    tagLength: 128,
  });
  cipher.update(forge.util.createBuffer(bytesToBinary(plaintext)));
  if (!cipher.finish()) {
    throw new Error("业务请求加密失败");
  }
  return binaryToBytes(cipher.output.getBytes() + cipher.mode.tag.getBytes());
};

const decryptWithForge = (keyBytes, iv, additionalData, ciphertextWithTag) => {
  const tagBytes = 16;
  if (ciphertextWithTag.length < tagBytes) {
    throw new Error("业务响应封套无效");
  }
  const ciphertext = ciphertextWithTag.subarray(0, ciphertextWithTag.length - tagBytes);
  const tag = ciphertextWithTag.subarray(ciphertextWithTag.length - tagBytes);
  const decipher = forge.cipher.createDecipher("AES-GCM", bytesToBinary(keyBytes));
  decipher.start({
    iv: bytesToBinary(iv),
    additionalData: bytesToBinary(additionalData),
    tag: bytesToBinary(tag),
    tagLength: 128,
  });
  decipher.update(forge.util.createBuffer(bytesToBinary(ciphertext)));
  if (!decipher.finish()) {
    throw new Error("业务响应认证失败");
  }
  return binaryToBytes(decipher.output.getBytes());
};

export const assertBusinessCryptoAvailable = () => {
  getCryptoConfiguration();
};

const createCryptoSession = async () => {
  const { kid, publicKey } = getCryptoConfiguration();
  const aesBytes = window.crypto.getRandomValues(new Uint8Array(32));
  let aesKey;
  let fallbackKeyBytes;
  let wrappedKey;
  const useWebCrypto = hasWebCrypto();
  try {
    if (useWebCrypto) {
      aesKey = await window.crypto.subtle.importKey(
        "raw",
        aesBytes,
        { name: "AES-GCM" },
        false,
        ["encrypt", "decrypt"]
      );
      const rsaKey = await window.crypto.subtle.importKey(
        "spki",
        fromBase64(publicKey),
        { name: "RSA-OAEP", hash: "SHA-256" },
        false,
        ["encrypt"]
      );
      wrappedKey = await window.crypto.subtle.encrypt(
        { name: "RSA-OAEP" },
        rsaKey,
        aesBytes
      );
    } else {
      fallbackKeyBytes = new Uint8Array(aesBytes);
      wrappedKey = wrapAesKeyWithForge(publicKey, aesBytes);
    }
  } catch (error) {
    if (fallbackKeyBytes) {
      fallbackKeyBytes.fill(0);
    }
    throw error;
  } finally {
    aesBytes.fill(0);
  }

  const wrappedKeyBytes = useWebCrypto
    ? new Uint8Array(wrappedKey)
    : binaryToBytes(wrappedKey);
  let result;
  try {
    const response = await axios.post(
      apiConfig.cryptoSession,
      { v: VERSION, kid, wrappedKey: toBase64(wrappedKeyBytes) },
      { headers: { "Content-Type": "application/json" }, timeout: 60000 }
    );
    result = response.data && response.data.data;
    if (
      !response.data ||
      response.data.code !== 0 ||
      !result ||
      result.v !== VERSION ||
      result.kid !== kid ||
      !result.sessionId ||
      !Number.isFinite(Number(result.expiresAt))
    ) {
      throw new Error("业务加密会话初始化失败");
    }
  } catch (error) {
    if (fallbackKeyBytes) {
      fallbackKeyBytes.fill(0);
    }
    throw error;
  } finally {
    wrappedKeyBytes.fill(0);
  }

  return {
    v: VERSION,
    kid,
    sessionId: result.sessionId,
    expiresAt: Number(result.expiresAt),
    cryptoMode: useWebCrypto ? "webcrypto" : "forge",
    key: aesKey,
    keyBytes: fallbackKeyBytes,
    pendingRequests: 0,
  };
};

const clearSessionKey = (session) => {
  if (!session || session === activeSession || session.pendingRequests > 0) {
    return;
  }
  if (session.keyBytes) {
    session.keyBytes.fill(0);
    session.keyBytes = null;
  }
  session.key = null;
};

export const getCryptoSession = async () => {
  if (activeSession && Date.now() < activeSession.expiresAt - SESSION_RENEW_WINDOW_MS) {
    return activeSession;
  }
  if (!sessionPromise) {
    const generation = sessionGeneration;
    let pendingSessionPromise;
    pendingSessionPromise = createCryptoSession()
      .then((session) => {
        if (generation === sessionGeneration) {
          const previousSession = activeSession;
          activeSession = session;
          clearSessionKey(previousSession);
        }
        return session;
      })
      .finally(() => {
        if (sessionPromise === pendingSessionPromise) {
          sessionPromise = null;
        }
      });
    sessionPromise = pendingSessionPromise;
  }
  return sessionPromise;
};

export const clearCryptoSession = (expectedSession) => {
  if (expectedSession && activeSession !== expectedSession) {
    return;
  }
  const previousSession = activeSession;
  sessionGeneration += 1;
  activeSession = null;
  sessionPromise = null;
  clearSessionKey(previousSession);
};

export const retainCryptoSession = (session) => {
  if (session) {
    session.pendingRequests = (session.pendingRequests || 0) + 1;
  }
};

export const releaseCryptoSession = (session) => {
  if (!session || !session.pendingRequests) {
    return;
  }
  session.pendingRequests -= 1;
  clearSessionKey(session);
};

export const getRequestTarget = (config, axiosInstance) => {
  const url = new URL(axiosInstance.getUri(config), window.location.origin);
  return `${url.pathname}${url.search}`;
};

export const isBusinessCryptoEndpoint = (method, requestTarget) => {
  const path = new URL(requestTarget, window.location.origin).pathname;
  return protectedEndpoints.has(`${method.toUpperCase()} ${path}`);
};

const buildAdditionalData = (session, method, requestTarget, requestId) => {
  const requestPath = new URL(requestTarget, window.location.origin).pathname;
  return new TextEncoder().encode(
    [VERSION, session.kid, session.sessionId, method, requestPath, requestId].join("\n")
  );
};

export const encryptBusinessRequest = async (
  session,
  method,
  requestTarget,
  requestId,
  payload
) => {
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const plaintext = new TextEncoder().encode(
    typeof payload === "string" ? payload : JSON.stringify(payload == null ? null : payload)
  );
  const additionalData = buildAdditionalData(
    session,
    method,
    requestTarget,
    requestId
  );
  const ciphertext =
    session.cryptoMode === "webcrypto"
      ? new Uint8Array(
          await window.crypto.subtle.encrypt(
            { name: "AES-GCM", iv, additionalData, tagLength: 128 },
            session.key,
            plaintext
          )
        )
      : encryptWithForge(session.keyBytes, iv, additionalData, plaintext);

  return {
    v: VERSION,
    kid: session.kid,
    sessionId: session.sessionId,
    requestId,
    iv: toBase64(iv),
    ciphertext: toBase64(ciphertext),
  };
};

export const decryptBusinessResponse = async (envelope, requestMeta) => {
  const session = requestMeta.session;
  if (
    !session ||
    !envelope ||
    envelope.v !== VERSION ||
    envelope.kid !== session.kid ||
    envelope.sessionId !== session.sessionId ||
    envelope.requestId !== requestMeta.requestId
  ) {
    clearCryptoSession(session);
    throw new Error("业务响应封套无效");
  }

  try {
    const iv = fromBase64(envelope.iv);
    const additionalData = buildAdditionalData(
      session,
      requestMeta.method,
      requestMeta.requestTarget,
      requestMeta.requestId
    );
    const plaintext =
      session.cryptoMode === "webcrypto"
        ? new Uint8Array(
            await window.crypto.subtle.decrypt(
              { name: "AES-GCM", iv, additionalData, tagLength: 128 },
              session.key,
              fromBase64(envelope.ciphertext)
            )
          )
        : decryptWithForge(
            session.keyBytes,
            iv,
            additionalData,
            fromBase64(envelope.ciphertext)
          );
    return JSON.parse(new TextDecoder().decode(plaintext));
  } catch (error) {
    clearCryptoSession(session);
    throw new Error("业务响应认证失败");
  }
};

export const setCryptoRequestHeaders = (headers, session, requestId) => {
  headers[HEADER_SESSION] = session.sessionId;
  headers[HEADER_REQUEST_ID] = requestId;
  headers[HEADER_KID] = session.kid;
};
